/**
 * Relewise: Custom Facets
 * Docs: https://docs.relewise.com/docs/search/faceting.html
 *
 * Configures facets for search functionality.
 */

import { getRelewiseFacetHeading } from './relewise-facet-headings';

/* eslint-disable @typescript-eslint/no-explicit-any */

type FacetConfig = {
  type: 'string' | 'boolean' | 'range' | 'price' | 'category';
  key?: string;
  level?: 'Product' | 'Variant';
  translationKey: string;
};

const DEFAULT_FACETS: FacetConfig[] = [
  //{ type: 'category', translationKey: 'categoryHeading' },
  {
    type: 'string',
    key: 'ProductType',
    level: 'Product',
    translationKey: 'product_type'
  } /*,
  {
    type: 'boolean',
    key: 'IsAvailable',
    level: 'Product',
    translationKey: 'availableHeading'
  }*/
];

const PRODUCTION_FACETS: FacetConfig[] = [
  {
    type: 'string',
    key: 'hobbii.knitting_gauge_stitches',
    level: 'Product',
    translationKey: 'knitting_gauge_stitches'
  },
  {
    type: 'string',
    key: 'hobbii.crochet_gauge_stitches',
    level: 'Product',
    translationKey: 'crochet_gauge_stitches'
  },
  {
    type: 'string',
    key: 'hobbii.yarn_composition_list_v2',
    level: 'Product',
    translationKey: 'yarn_composition_list_v2'
  },
  {
    type: 'string',
    key: 'hobbii.yarn_type_filter_categories',
    level: 'Product',
    translationKey: 'yarn_type_filter_categories'
  },
  {
    type: 'string',
    key: 'hobbii.yarn_weight_category',
    level: 'Product',
    translationKey: 'yarn_weight_category'
  },
  {
    type: 'string',
    key: 'hobbii.pattern_kind',
    level: 'Product',
    translationKey: 'pattern_kind'
  } /*
  {
    type: 'range',
    key: 'hobbii.yarn_length',
    level: 'Product',
    translationKey: 'yarn_length'
  }*/,
  {
    type: 'string',
    key: 'hobbii.crochet_hooks_filter_categories',
    level: 'Product',
    translationKey: 'crochet_hooks_filter_categories'
  },
  {
    type: 'string',
    key: 'hobbii.brand',
    level: 'Product',
    translationKey: 'brand'
  },
  {
    type: 'string',
    key: 'hobbii.designer',
    level: 'Product',
    translationKey: 'designer'
  },
  {
    type: 'string',
    key: 'hobbii.difficulty',
    level: 'Product',
    translationKey: 'difficulty'
  },
  {
    type: 'string',
    key: 'hobbii.wear_size',
    level: 'Variant',
    translationKey: 'wear_size'
  },
  {
    type: 'string',
    key: 'hobbii.gauge_stitches',
    level: 'Product',
    translationKey: 'gauge_stitches'
  },
  {
    type: 'string',
    key: 'hobbii.madefor',
    level: 'Product',
    translationKey: 'madefor'
  },
  {
    type: 'string',
    key: 'hobbii.generic_color',
    level: 'Product',
    translationKey: 'generic_color'
  },
  {
    type: 'string',
    key: 'hobbii.recommended_needles_list_v2',
    level: 'Product',
    translationKey: 'recommended_needles_list_v2'
  },
  {
    type: 'string',
    key: 'hobbii.recommended_hooks_list_v2',
    level: 'Product',
    translationKey: 'recommended_hooks_list_v2'
  },
  {
    type: 'string',
    key: 'hobbii.sizes',
    level: 'Variant',
    translationKey: 'sizes'
  } /*,
  {
    type: 'string',
    key: 'hobbii.product_group_id',
    level: 'Product',
    translationKey: 'product_group_id'
  }*/,
  {
    type: 'string',
    key: 'hobbii.hook_sizes',
    level: 'Variant',
    translationKey: 'hook_sizes'
  },
  {
    type: 'string',
    key: 'hobbii.hook_sizes_uk',
    level: 'Variant',
    translationKey: 'hook_sizes_uk'
  },
  {
    type: 'string',
    key: 'hobbii.hook_sizes_us',
    level: 'Variant',
    translationKey: 'hook_sizes_us'
  }
];

function addFacetToBuilder(builder: any, config: FacetConfig) {
  const getHeading = (key: string) => {
    // Check if it's a localization heading or a translation key
    if (
      ['categoryHeading', 'availableHeading', 'vendorHeading'].includes(key)
    ) {
      return window.RelewiseLocalization?.facets?.[
        key as keyof typeof window.RelewiseLocalization.facets
      ];
    }
    return getRelewiseFacetHeading(window.Shopify.locale, key);
  };

  const heading = getHeading(config.translationKey);

  switch (config.type) {
    case 'category':
      builder.addFacet((f: any) => f.addCategoryFacet('ImmediateParent'), {
        heading
      });
      break;

    case 'string':
      builder.addFacet(
        (f: any) =>
          f.addProductDataStringValueFacet(
            config.key!.startsWith('hobbii.')
              ? `${window.Shopify.shop}_${config.key}`
              : config.key,
            config.level
          ),
        { heading }
      );
      break;

    case 'boolean':
      builder.addFacet(
        (f: any) => f.addProductDataBooleanValueFacet(config.key, config.level),
        { heading }
      );
      break;

    case 'range':
      builder.addFacet(
        (f: any) =>
          f.addProductDataDoubleRangeFacet(
            `${window.Shopify.shop}_${config.key}`,
            config.level
          ),
        { heading }
      );
      break;

    case 'price':
      builder.addFacet((f: any) => f.addSalesPriceRangeFacet('Product'), {
        heading
      });
      break;
  }
}

function registerFacets(facets: FacetConfig[]) {
  window.RelewiseWebComponents!.registerSearchTarget('search', {
    overwriteFacets(builder: any) {
      // Add default facets
      DEFAULT_FACETS.forEach((config) => addFacetToBuilder(builder, config));

      // Add environment-specific facets
      facets.forEach((config) => addFacetToBuilder(builder, config));
    }
  });
}

/**
 * Initialize accordion behavior for facets
 */
function initFacetAccordion() {
  const facets = document.querySelector('relewise-facets');
  if (!facets) return;

  // Bind accordion behavior once the facets shadow DOM is ready
  const wire = () => {
    const containerOuter = facets.shadowRoot?.querySelector<HTMLElement>(
      '.rw-facets-container'
    );
    const containers =
      facets.shadowRoot?.querySelectorAll<HTMLElement>('[part="container"]') ||
      [];

    if (containerOuter?.style) {
      containerOuter.style.gap = '0';
      containerOuter.style.padding = '0';
    }

    containers.forEach((container) => {
      const el = container;
      if (el.dataset.accordionBound) return;

      el.dataset.accordionBound = 'true';
      el.tabIndex = 0; // allow keyboard toggling
      el.setAttribute('role', 'button'); // basic semantics for focusable header area
      if (el.style) el.style.padding = '0';

      // Toggle visual open/closed state via CSS custom properties
      const apply = (open: boolean) => {
        el.style.setProperty(
          '--relewise-facet-overflow',
          open ? 'visible' : 'hidden'
        );
        el.style.setProperty('--relewise-facet-padding', open ? '1.5rem' : '0');
        el.style.setProperty(
          '--relewise-facet-max-height',
          open ? '100%' : '56px'
        );
        el.style.setProperty(
          '--relewise-arrow-deg',
          open
            ? 'translateY(-50%) rotate(45deg)'
            : 'translateY(-50%) rotate(-135deg)'
        );
        el.setAttribute('aria-expanded', String(open));
        el.dataset.accordionOpen = open ? 'true' : 'false';
      };

      apply(false);

      // Style the "Show More" control inside the facet's inner shadow root
      const styleShowMore = () => {
        const btn = el.shadowRoot?.querySelector<HTMLElement>('.rw-show-more');
        if (btn && !btn.dataset.styled) {
          const btnEl = btn as HTMLElement;
          btnEl.dataset.styled = 'true';
          btnEl.style.color = 'var(--relewise-show-more-color)';
          btnEl.style.fontWeight = 'var(--relewise-show-more-font-weight)';
          btnEl.style.textDecoration =
            'var(--relewise-show-more-text-decoration)';
          btnEl.style.background = 'var(--relewise-show-more-background)';
          btnEl.style.border = 'var(--relewise-show-more-border)';
          btnEl.style.borderRadius = 'var(--relewise-show-more-border-radius)';
          btnEl.style.padding = 'var(--relewise-show-more-padding)';
          btnEl.style.margin = 'var(--relewise-show-more-margin)';
          btnEl.style.height = 'var(--relewise-show-more-height)';

          // To make sure the active accordion does not close when clicking the button
          btnEl.addEventListener('click', (e) => e.stopPropagation());
          btnEl.addEventListener('keydown', (e) => e.stopPropagation());
        }
      };

      styleShowMore();

      if (el.shadowRoot) {
        new MutationObserver(styleShowMore).observe(el.shadowRoot, {
          childList: true,
          subtree: true
        });
      }

      // Toggle only when the click isn't on interactive elements, labels, or the Show More button
      const toggle = (event: Event) => {
        const inShowMore = event
          .composedPath()
          .some(
            (elem) =>
              elem instanceof HTMLElement &&
              elem.classList.contains('rw-show-more')
          );
        if (inShowMore) return;

        const path = event.composedPath();
        const inLabel = path.find(
          (elem) =>
            elem instanceof HTMLElement &&
            (elem.tagName.toLowerCase() === 'label' ||
              elem.getAttribute('part') === 'label')
        );
        if (inLabel) return;

        const interactive = path.find(
          (elem) =>
            elem instanceof HTMLElement &&
            elem.matches(
              'input, select, textarea, option, button, a, [role="button"], [role="checkbox"], [role="switch"]'
            )
        );
        if (interactive && interactive !== el) return;

        apply(el.dataset.accordionOpen !== 'true');
      };

      el.addEventListener('click', toggle);
      el.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggle(event);
        }
      });
    });
  };

  const pollShadow = () => {
    if (facets.shadowRoot) {
      wire();
      new MutationObserver(wire).observe(facets.shadowRoot, {
        childList: true,
        subtree: true
      });
    } else {
      requestAnimationFrame(pollShadow);
    }
  };

  pollShadow();
}

/**
 * Registers the facets with the Relewise search target registry.
 * Returns false when the library or its registry is not available yet.
 */
export function registerRelewiseFacets(): boolean {
  if (
    typeof window.RelewiseWebComponents === 'undefined' ||
    typeof window.relewiseUISearchTargetedConfigurations === 'undefined'
  ) {
    return false;
  }

  registerFacets(PRODUCTION_FACETS);
  return true;
}

export function initRelewiseFacets() {
  customElements
    .whenDefined('relewise-facets')
    .then(initFacetAccordion)
    .catch(() => {});
}
/* eslint-enable @typescript-eslint/no-explicit-any */
